import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, authFetchJson } from "@/lib/api";

const POLL_INTERVAL_MS = 30_000;

type Maintenance = {
  reboot_required: boolean;
  reboot_required_since: string | null;
  reboot_required_packages: string | null;
  apt_last_success: string | null;
  probe_updated: string | null;
};

type Node = {
  name: string;
  ready: boolean;
  roles: string[];
  kubelet_version: string;
  age_seconds: number;
  cpu_used_millicores: number | null;
  cpu_capacity_millicores: number;
  mem_used_bytes: number | null;
  mem_capacity_bytes: number;
  maintenance: Maintenance;
};

type MaintenanceWindow = {
  days: string[];
  start: string;
  end: string;
  timezone: string;
  next_start: string;
  next_end: string;
  in_window_now: boolean;
};

type Pod = {
  namespace: string;
  name: string;
  phase: "Pending" | "Running" | "Succeeded" | "Failed" | "Unknown";
  ready: string;
  restarts: number;
  last_restart_reason: string | null;
  age_seconds: number;
  node: string;
  cpu_used_millicores: number | null;
  mem_used_bytes: number | null;
};

type Deployment = {
  namespace: string;
  name: string;
  ready_replicas: number;
  desired_replicas: number;
  available: boolean;
  image: string;
};

type ClusterEvent = {
  namespace: string;
  type: "Normal" | "Warning";
  reason: string;
  object: string;
  message: string;
  count: number;
  last_seen: string;
};

type Overview = {
  generated_at: string;
  nodes: Node[];
  pods: Pod[];
  deployments: Deployment[];
  events: ClusterEvent[];
  maintenance: { window: MaintenanceWindow | null };
};

const HIDDEN_NAMESPACES = new Set(["kube-system", "kube-public", "kube-node-lease", "observability"]);

function fmtAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function fmtRelative(iso: string, now: number): string {
  const ageMs = now - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(ageMs / 1000));
  if (s < 60) return `${s}s geleden`;
  if (s < 3600) return `${Math.floor(s / 60)}m geleden`;
  return `${Math.floor(s / 3600)}h geleden`;
}

function fmtMillicores(m: number | null): string {
  if (m === null) return "—";
  if (m < 1000) return `${m}m`;
  return `${(m / 1000).toFixed(2)} cores`;
}

function fmtBytes(b: number | null): string {
  if (b === null) return "—";
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(0)} KiB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(0)} MiB`;
  return `${(b / 1024 ** 3).toFixed(2)} GiB`;
}

function pctClass(pct: number): string {
  if (pct >= 90) return "bg-red-500";
  if (pct >= 70) return "bg-amber-500";
  return "bg-emerald-500";
}

const DAY_LABELS_NL: Record<string, string> = {
  mon: "ma", tue: "di", wed: "wo", thu: "do", fri: "vr", sat: "za", sun: "zo",
};

function fmtDays(days: string[]): string {
  return days.map((d) => DAY_LABELS_NL[d] ?? d).join("–");
}

function fmtAbsolute(iso: string): string {
  return new Date(iso).toLocaleString("nl-BE", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDeltaUntil(iso: string, now: number): string {
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return "nu";
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (h < 48) return `~${h}u`;
  return `${Math.floor(h / 24)}d`;
}

function UsageBar({ used, capacity }: { used: number | null; capacity: number }) {
  if (used === null || capacity === 0) return <span className="text-[var(--cs-ink-muted)]">—</span>;
  const pct = Math.min(100, Math.round((used / capacity) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[rgba(10,52,96,0.08)]">
        <div className={`h-full ${pctClass(pct)}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="tabular-nums text-[11px] text-[var(--cs-ink-muted)]">{pct}%</span>
    </div>
  );
}

export function AdminClusterTab() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [namespaceFilter, setNamespaceFilter] = useState<string>("__all__");
  const [hideSystem, setHideSystem] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetchJson<Overview>("/api/admin/cluster/overview");
      setData(res);
      setLoadError("");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Laden mislukt.";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [load]);

  // Poll every 30s while the tab is visible.
  useEffect(() => {
    const start = () => {
      if (intervalRef.current !== null) return;
      intervalRef.current = window.setInterval(() => { void load(); }, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const onVisibility = () => { if (document.hidden) stop(); else start(); };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  const namespaces = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set(data.pods.map((p) => p.namespace));
    return Array.from(set).sort();
  }, [data]);

  const visiblePods = useMemo(() => {
    if (!data) return [] as Pod[];
    return data.pods.filter((p) => {
      if (namespaceFilter !== "__all__" && p.namespace !== namespaceFilter) return false;
      if (hideSystem && HIDDEN_NAMESPACES.has(p.namespace)) return false;
      return true;
    });
  }, [data, namespaceFilter, hideSystem]);

  const metricsMissing = useMemo(() => {
    if (!data || data.nodes.length === 0) return false;
    return data.nodes.every((n) => n.cpu_used_millicores === null);
  }, [data]);

  const generatedAgeSec = data ? Math.max(0, Math.floor((now - new Date(data.generated_at).getTime()) / 1000)) : 0;
  const nextRefreshIn = Math.max(0, Math.ceil((POLL_INTERVAL_MS - (generatedAgeSec * 1000)) / 1000));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--cs-ink-muted)]">
          {data
            ? `Vernieuwd: ${generatedAgeSec}s geleden • Volgende: in ${nextRefreshIn}s`
            : "Laden…"}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
        >
          Vernieuw nu
        </button>
      </div>

      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {loadError}
        </div>
      ) : null}

      {loading && !data ? <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p> : null}

      {data ? (
        <>
          {metricsMissing ? (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Resourcegebruik tijdelijk niet beschikbaar (metrics-server).
            </p>
          ) : null}

          {/* NODES */}
          <Section title="Nodes">
            <table className="w-full text-sm">
              <Thead cols={["Naam", "Status", "Rol", "Versie", "Age", "CPU", "Geheugen"]} />
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {data.nodes.map((n) => (
                  <tr key={n.name} className="text-xs hover:bg-[var(--cs-ivory)]/60">
                    <td className="px-4 py-2 font-medium text-[var(--cs-navy)]">{n.name}</td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`h-2 w-2 rounded-full ${n.ready ? "bg-emerald-500" : "bg-red-500"}`} />
                        {n.ready ? "Ready" : "NotReady"}
                      </span>
                    </td>
                    <td className="px-4 py-2">{n.roles.join(", ")}</td>
                    <td className="px-4 py-2 text-[var(--cs-ink-muted)]">{n.kubelet_version}</td>
                    <td className="px-4 py-2 tabular-nums">{fmtAge(n.age_seconds)}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1">
                        <span className="tabular-nums text-[11px]">
                          {fmtMillicores(n.cpu_used_millicores)} / {fmtMillicores(n.cpu_capacity_millicores)}
                        </span>
                        <UsageBar used={n.cpu_used_millicores} capacity={n.cpu_capacity_millicores} />
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1">
                        <span className="tabular-nums text-[11px]">
                          {fmtBytes(n.mem_used_bytes)} / {fmtBytes(n.mem_capacity_bytes)}
                        </span>
                        <UsageBar used={n.mem_used_bytes} capacity={n.mem_capacity_bytes} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ONDERHOUD */}
          <Section title="Onderhoud">
            <MaintenanceHeader window={data.maintenance?.window ?? null} now={now} />
            <table className="w-full text-sm">
              <Thead cols={["Node", "Reboot vereist", "Laatste apt run", "Probe"]} />
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {data.nodes.map((n) => {
                  const m = n.maintenance;
                  const probeAgeMin = m.probe_updated
                    ? Math.floor((now - new Date(m.probe_updated).getTime()) / 60_000)
                    : null;
                  const probeStale = probeAgeMin !== null && probeAgeMin > 90;
                  return (
                    <tr key={n.name} className={`text-xs hover:bg-[var(--cs-ivory)]/60 ${m.reboot_required ? "bg-amber-50" : ""}`}>
                      <td className="px-4 py-2 font-medium text-[var(--cs-navy)]">{n.name}</td>
                      <td className="px-4 py-2">
                        {m.reboot_required ? (
                          <div>
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                              ja
                              {m.reboot_required_since
                                ? ` · sinds ${fmtRelative(m.reboot_required_since, now)}`
                                : ""}
                            </span>
                            {m.reboot_required_packages ? (
                              <p className="mt-0.5 text-[10px] text-[var(--cs-ink-muted)]">
                                {m.reboot_required_packages}
                              </p>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-[var(--cs-ink-muted)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-[var(--cs-ink-muted)]" title={m.apt_last_success ?? ""}>
                        {m.apt_last_success ? fmtRelative(m.apt_last_success, now) : "—"}
                      </td>
                      <td className={`px-4 py-2 ${probeStale ? "text-amber-700" : "text-[var(--cs-ink-muted)]"}`}>
                        {probeAgeMin === null ? "nooit" : probeAgeMin < 1 ? "<1 min" : `${probeAgeMin} min`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>

          {/* PODS */}
          <Section
            title="Pods"
            controls={
              <div className="flex items-center gap-3 text-xs">
                <select
                  value={namespaceFilter}
                  onChange={(e) => setNamespaceFilter(e.target.value)}
                  className="rounded border border-[rgba(10,52,96,0.18)] bg-white px-2 py-1"
                >
                  <option value="__all__">Alle namespaces</option>
                  {namespaces.map((ns) => (
                    <option key={ns} value={ns}>{ns}</option>
                  ))}
                </select>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={hideSystem}
                    onChange={(e) => setHideSystem(e.target.checked)}
                  />
                  Verberg kube-system / observability
                </label>
              </div>
            }
          >
            <table className="w-full text-sm">
              <Thead cols={["Namespace", "Naam", "Phase", "Ready", "Restarts", "Age", "Node", "CPU", "Geheugen"]} />
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {visiblePods.map((p) => {
                  const unhealthy = p.phase !== "Running" || p.restarts > 0;
                  return (
                    <tr
                      key={`${p.namespace}/${p.name}`}
                      className={`text-xs hover:bg-[var(--cs-ivory)]/60 ${unhealthy ? "bg-amber-50" : ""}`}
                    >
                      <td className="px-4 py-2 text-[var(--cs-ink-muted)]">{p.namespace}</td>
                      <td className="px-4 py-2 font-medium text-[var(--cs-navy)]">{p.name}</td>
                      <td className="px-4 py-2">
                        <PhaseBadge phase={p.phase} />
                      </td>
                      <td className="px-4 py-2 tabular-nums">{p.ready}</td>
                      <td className="px-4 py-2 tabular-nums" title={p.last_restart_reason ?? undefined}>
                        {p.restarts}
                        {p.last_restart_reason ? <span className="ml-1 text-[10px] text-amber-700">({p.last_restart_reason})</span> : null}
                      </td>
                      <td className="px-4 py-2 tabular-nums">{fmtAge(p.age_seconds)}</td>
                      <td className="px-4 py-2 text-[var(--cs-ink-muted)]">{p.node}</td>
                      <td className="px-4 py-2 tabular-nums">{fmtMillicores(p.cpu_used_millicores)}</td>
                      <td className="px-4 py-2 tabular-nums">{fmtBytes(p.mem_used_bytes)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>

          {/* DEPLOYMENTS */}
          <Section title="Deployments">
            <table className="w-full text-sm">
              <Thead cols={["Namespace", "Naam", "Ready", "Image"]} />
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {data.deployments.map((d) => {
                  const broken = d.ready_replicas < d.desired_replicas || !d.available;
                  return (
                    <tr
                      key={`${d.namespace}/${d.name}`}
                      className={`text-xs hover:bg-[var(--cs-ivory)]/60 ${broken ? "bg-red-50" : ""}`}
                    >
                      <td className="px-4 py-2 text-[var(--cs-ink-muted)]">{d.namespace}</td>
                      <td className="px-4 py-2 font-medium text-[var(--cs-navy)]">{d.name}</td>
                      <td className="px-4 py-2 tabular-nums">
                        {d.ready_replicas}/{d.desired_replicas}
                      </td>
                      <td className="px-4 py-2 text-[10px] text-[var(--cs-ink-muted)] break-all">{d.image}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>

          {/* EVENTS */}
          <Section title="Recente events (laatste uur)">
            <table className="w-full text-sm">
              <Thead cols={["Wanneer", "Type", "Object", "Reden", "Bericht", "Aantal"]} />
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {data.events.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-3 text-xs text-[var(--cs-ink-muted)]">Geen events.</td></tr>
                ) : null}
                {data.events.map((e, i) => {
                  const expanded = expandedEvent === i;
                  return (
                    <tr key={`${e.last_seen}-${i}`} className="text-xs hover:bg-[var(--cs-ivory)]/60">
                      <td className="px-4 py-2 tabular-nums text-[var(--cs-ink-muted)]">{fmtRelative(e.last_seen, now)}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          e.type === "Warning" ? "bg-amber-100 text-amber-700" : "bg-[var(--cs-ivory)] text-[rgba(10,52,96,0.78)]"
                        }`}>
                          {e.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-[var(--cs-navy)]">{e.object}</td>
                      <td className="px-4 py-2">{e.reason}</td>
                      <td
                        className="cursor-pointer px-4 py-2 text-[var(--cs-ink-muted)]"
                        onClick={() => setExpandedEvent(expanded ? null : i)}
                      >
                        {expanded ? e.message : (e.message.length > 80 ? e.message.slice(0, 80) + "…" : e.message)}
                      </td>
                      <td className="px-4 py-2 tabular-nums">{e.count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>
        </>
      ) : null}
    </div>
  );
}

function Section({ title, controls, children }: {
  title: string;
  controls?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">{title}</h3>
        {controls}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
        {children}
      </div>
    </section>
  );
}

function Thead({ cols }: { cols: string[] }) {
  return (
    <thead className="bg-[var(--cs-ivory)] text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
      <tr>
        {cols.map((c) => (
          <th key={c} className="px-4 py-2 text-left">{c}</th>
        ))}
      </tr>
    </thead>
  );
}

function MaintenanceHeader({ window, now }: { window: MaintenanceWindow | null; now: number }) {
  if (!window) {
    return (
      <div className="border-b border-[rgba(10,52,96,0.06)] bg-[var(--cs-ivory)] px-4 py-2 text-xs text-[var(--cs-ink-muted)]">
        Reboot venster: onbekend (kured DaemonSet niet leesbaar). Apt-daily-upgrade: dagelijks ~06:00 via unattended-upgrades.
      </div>
    );
  }
  const daysLabel = fmtDays(window.days);
  const nextLine = window.in_window_now
    ? "Volgende: nu actief"
    : `Volgende: ${fmtAbsolute(window.next_start)} (in ${fmtDeltaUntil(window.next_start, now)})`;
  return (
    <div className="space-y-0.5 border-b border-[rgba(10,52,96,0.06)] bg-[var(--cs-ivory)] px-4 py-2 text-xs text-[rgba(10,52,96,0.78)]">
      <p>
        Reboot venster: <strong>{daysLabel} {window.start}–{window.end}</strong> {window.timezone} · {nextLine}
      </p>
      <p className="text-[var(--cs-ink-muted)]">
        Apt-daily-upgrade: dagelijks ~06:00 via unattended-upgrades.
      </p>
    </div>
  );
}

function PhaseBadge({ phase }: { phase: Pod["phase"] }) {
  const color =
    phase === "Running" ? "bg-emerald-100 text-emerald-700"
    : phase === "Pending" ? "bg-amber-100 text-amber-700"
    : phase === "Succeeded" ? "bg-blue-100 text-blue-700"
    : "bg-red-100 text-red-700";
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${color}`}>{phase}</span>;
}
